import { getTypeOfPolicy, isAgentPortal, isFlagEnabled } from "js/utilities";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { useState, useEffect } from "react";

import { Link } from "react-router-dom";
import {
  getBillingTypeCodeMapping,
  getManagePaymentStatus,
  getManagePaymentStatusText,
} from "js/formatting";

import Badge from "components/General/Badge";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import Tooltip from "components/General/Tooltip";

export default function PaymentRow({
  data,
  type,
  policies,
  hasStatement = true,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isOpenCancelOnetimeDialog, setIsOpenCancelOnetimeDialog] =
    useState(false);
  const [isOpenEditPaymentDialog, setIsOpenEditPaymentDialog] = useState(false);
  const [isOpenCancelAutopayDialog, setIsOpenCancelAutopayDialog] =
    useState(false);
  const [status, setStatus] = useState();
  const [statusText, setStatusText] = useState();
  const [policy, setPolicy] = useState();
  useEffect(() => {
    if (policies?.length > 0) {
      setPolicy(policies?.find((p) => p.contractID === data?.contractId));
    }
  }, [policies]);

  useEffect(() => {
    if (data?.autoPayment) {
      let paymentStatus = getManagePaymentStatus(data?.autoPayment);
      let paymentStatusText = getManagePaymentStatusText(data?.autoPayment);
      setStatus(paymentStatus);
      setStatusText(
        paymentStatus === "good" ? paymentStatusText : `CC ${paymentStatusText}`
      );
    }
  }, [data]);
  return (
    <>
      <div className="ui-card-bar relative mt-4">
        <div className={hasStatement ? `px-3 py-2 lg:p-4` : `px-3 py-4`}>
          <div
            className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${
              hasStatement
                ? "md:grid-cols-8 xl:gap-6"
                : "md:grid-cols-6 xl:gap-2"
            }`}
          >
            <div className={hasStatement ? `md:col-span-4` : `md:col-span-2`}>
              {hasStatement ? (
                <>
                  <div className="text-xs text-theme-text-tertiary mb-1">
                    {getTypeOfPolicy(policy?.product?.type) === "annuity"
                      ? t("CustomerPaymentRow-ContractText")
                      : t("CustomerPaymentRow-PolicyText")}
                    {data?.oldPolicy}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data?.policyName}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-theme-text-secondary mb-1">
                    {data?.oldPolicy}
                  </div>
                </>
              )}
            </div>
            <div className={hasStatement ? "" : "md:col-span-1"}>
              {hasStatement && (
                <div className="text-xs text-theme-text-tertiary mb-1">
                  {t("CustomerPaymentRow-AmountText")}
                </div>
              )}
              <div className="text-sm font-semibold text-theme-text-secondary">
                {data?.amount} {data.type === "Disbursed" && " *"}
              </div>
            </div>

            {type === "scheduled" && hasStatement && (
              <>
                <div className="">
                  <div className="text-xs text-theme-text-tertiary mb-1">
                    {t("CustomerPaymentRow-scheduledDateText")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data?.scheduledDate}
                  </div>
                </div>
                {data?.autoPayment && (
                  <div className="">
                    <div className="text-xs text-theme-text-tertiary mb-1">
                      {t("CustomerPaymentRow-autopayStatusText")}
                    </div>
                    <div className="flex text-sm font-semibold text-theme-text-secondary">
                      <Badge
                        mediaSwitch={false}
                        status={status}
                        statusText={statusText}
                        srPrefix=""
                      />
                      <span className="mx-2">/</span>
                      <button
                        className="ui-btn-hyperlink"
                        onClick={() => navigate(`/payment/manage-payments`)}
                      >
                        <Trans
                          i18nKey="CustomerPaymentRow-managePaymentButton"
                          values={{ policyId: data?.policyId }}
                          components={{
                            span1: <span className="sr-only" />,
                          }}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            {type === "history" && (
              <>
                <div className={hasStatement ? "" : "md:col-span-1"}>
                  {hasStatement && (
                    <div className="text-xs text-theme-text-tertiary mb-1">
                      {t("CustomerPaymentRow-methodText")}
                    </div>
                  )}
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {getBillingTypeCodeMapping(data?.paymentMethod)}
                  </div>
                </div>
                <div className={hasStatement ? "" : "md:col-span-1"}>
                  {hasStatement && (
                    <div className="text-xs text-theme-text-tertiary mb-1">
                      {t("CustomerPaymentRow-statusText")}
                    </div>
                  )}
                  <div className="text-sm font-semibold text-theme-text-secondary flex items-center">
                    {data.type}
                    {data.type === "Returned" &&
                      isFlagEnabled(
                        "ENABLE_TOOLTIP_FOR_RETURNED_EFT_STATUS"
                      ) && (
                        <Tooltip
                          xMargin="mx-0"
                          content={
                            <>
                              {t("CustomerPaymentRow-tooltip-contentText")}
                              <Link
                                className="underline text-theme-action"
                                to={`/messages?tab=correspondence&id=${data.oldPolicy}`}
                              >
                                {t(
                                  "CustomerPaymentRow-tooltip-correspondenceLink"
                                )}
                              </Link>
                              .
                            </>
                          }
                        />
                      )}
                  </div>
                </div>
                <div className={hasStatement ? "" : "md:col-span-1"}>
                  {hasStatement && (
                    <div className="text-xs text-theme-text-tertiary">
                      {t("CustomerPaymentRow-dateText")}
                    </div>
                  )}
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    <div>{data.postedDate}</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={isOpenEditPaymentDialog}
        onClose={() => setIsOpenEditPaymentDialog(false)}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {t("CustomerPaymentRow-editPaymentDialog-title")}
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label={t("CustomerPaymentRow-editPaymentDialog-ariaLabel")}
              onClick={() => setIsOpenEditPaymentDialog(false)}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <hr />
            <Dialog.Description>
              {t("CustomerPaymentRow-editPaymentDialog-description")}
            </Dialog.Description>

            <div className="text-xl my">
              <p>
                Work with Bryan to reuse Make a Payment for Wireframe pgs. 28 -
                33
              </p>
              <p>
                Wireframes so new screens for payment form vs the dialog in mock
              </p>
              <p>*** Need to ask Jay</p>
            </div>

            <div className="block text-right mt-6">
              <button
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={() => setIsOpenEditPaymentDialog(false)}
              >
                {t("CustomerPaymentRow-editPaymentDialog-closeButton")}
              </button>
              <button
                id="ID_applyFilterOptions"
                className="ui-btn-primary ui-btn-min-w mt-4"
                onClick={() => setIsOpenEditPaymentDialog(false)}
              >
                {t("CustomerPaymentRow-editPaymentDialog-closeButton")}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={isOpenCancelOnetimeDialog}
        onClose={() => setIsOpenCancelOnetimeDialog(false)}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {t("CustomerPaymentRow-cancelPaymentDialog-title")}
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label={t("CustomerPaymentRow-cancelPaymentDialog-ariaLabel")}
              onClick={() => setIsOpenCancelOnetimeDialog(false)}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <hr />
            <Dialog.Description>
              {t("CustomerPaymentRow-cancelPaymentDialog-description")}
            </Dialog.Description>
            <div className="block text-right mt-6">
              <button
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={() => setIsOpenCancelOnetimeDialog(false)}
              >
                {t("CustomerPaymentRow-cancelPaymentDialog-scheduledButton")}
              </button>
              <button
                id="ID_applyFilterOptions"
                className="ui-btn-primary ui-btn-min-w mt-4"
                onClick={() => setIsOpenCancelOnetimeDialog(false)}
              >
                {t(
                  "CustomerPaymentRow-cancelPaymentDialog-cancelPaymentButton"
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={isOpenCancelAutopayDialog}
        onClose={() => setIsOpenCancelAutopayDialog(false)}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {t("CustomerPaymentRow-cancelAutopayDialog-title")}
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label={t("CustomerPaymentRow-cancelAutopayDialog-ariaLabel")}
              onClick={() => setIsOpenCancelAutopayDialog(false)}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <hr />
            <Dialog.Description>
              {t("CustomerPaymentRow-cancelAutopayDialog-description")}
            </Dialog.Description>
            <div className="block text-right mt-6">
              <button
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={() => setIsOpenCancelAutopayDialog(false)}
              >
                <Trans
                  i18nKey="CustomerPaymentRow-cancelAutopayDialog-dontCancelButton"
                  components={{
                    div: <div className="text-xs" />,
                  }}
                />
              </button>
              <button
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={() => setIsOpenCancelAutopayDialog(false)}
              >
                <Trans
                  i18nKey="CustomerPaymentRow-cancelAutopayDialog-cancelRecurringPaymentButton"
                  components={{
                    div: <div className="text-xs" />,
                  }}
                />
              </button>
              <button
                id="ID_applyFilterOptions"
                className="ui-btn-primary ui-btn-min-w mt-4"
                onClick={() => setIsOpenCancelAutopayDialog(false)}
              >
                <Trans
                  i18nKey="CustomerPaymentRow-cancelAutopayDialog-cancelOnePaymentButton"
                  components={{
                    div: <div className="text-xs" />,
                  }}
                />
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
